import type { IntlShape } from 'react-intl';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import { makeStatusMapper } from '../../../../contracts/projects/timeline/makeStatusMapper';
import { readToolResultText } from '../../../../contracts/toolResult';
import { friendlyToolError } from '../../components/projectChatMessage.helpers';
import { completedTodoLabel, failedTodoLabel, runningTodoLabel } from './agentTodoLabels';
import { buildTodoSubtitle } from './buildTodoSubtitle';
import type { LatestTodoPlan } from './computeLatestTodoPlan';
import { mergeTodoItemsWithLatest } from './mergeTodoItemStatuses';
import {
  countTodosInInput,
  parseAgentTodoResult,
  readTodoOperation,
} from './parseAgentTodoResult';
import { planCardTitle } from './planCardTitle';
import { planStateAt, showsPlanCard } from './planCardVisibility';
import { planCreatePresentation } from './planCreatePresentation';
import { todoLabelCount } from './todoLabelCount';
import type { AgentTodoItem } from './types';
import { CREATE_TODO_PLAN_TOOL_NAME, TODO_TOOL_NAME } from './types';

const toAgentTodoStatus = makeStatusMapper('running', 'completed', 'failed');

export interface PlanCardModel {
  kind: 'plan-card';
  title: string;
  items: AgentTodoItem[];
  anchorId?: string;
  declined?: boolean;
  /** The turn that last advanced this card ended with a stop or a failure. */
  stopped: boolean;
}

export interface AgentTodoResultBlockModel {
  kind: 'result-block';
  status: 'running' | 'completed' | 'failed';
  title: string;
  subtitle?: string;
  items: AgentTodoItem[];
  error?: string;
}

export type TodoToolUseModel = PlanCardModel | AgentTodoResultBlockModel;

/**
 * The latest plan's anchor renders the live card (with the anchor handle the
 * pinned summary observes); a superseded plan's anchor renders its final state
 * as a frozen card. Any other row renders nothing.
 */
const resolvePlanCardAtAnchor = (
  item: ToolUseItem,
  plan: LatestTodoPlan,
  intl: IntlShape,
  projectTitle?: string
): PlanCardModel | null => {
  const isLatest = plan.anchorId === item.id;
  const cardState = isLatest ? plan.state : plan.supersededByAnchorId.get(item.id);
  if (!cardState) return null;
  return {
    kind: 'plan-card',
    title: planCardTitle(cardState, intl, projectTitle),
    items: cardState.items,
    anchorId: isLatest ? plan.anchorId : undefined,
    stopped: plan.stoppedAnchorIds.has(item.id),
  };
};

export interface RenderTodoToolUseOptions {
  /**
   * Latest known status per todo id from the full timeline — forward-fills
   * completed/cancelled on older todo blocks (legacy, pre-`todo_state` path).
   */
  latestTodoItemsById?: ReadonlyMap<number, AgentTodoItem>;
  /**
   * Current plan derived from `todo_state` results. When set, the timeline is
   * in the plan-card world from the plan segment onwards: one card at the
   * anchor, no per-op todo rows. Rows in `plan.legacyIds` predate the segment
   * and keep the legacy rendering.
   */
  latestTodoPlan?: LatestTodoPlan | null;
  /** Chat title — plan-card header fallback when the plan has no title. */
  projectTitle?: string;
  /**
   * The row's own turn ended with a stop or a failure. Cards outside the plan
   * (declined, in flight) have no plan state to read it from.
   */
  turnStopped?: boolean;
}

export const resolveTodoToolUse = (
  item: ToolUseItem,
  intl: IntlShape,
  options?: RenderTodoToolUseOptions
): TodoToolUseModel | null => {
  const {
    latestTodoItemsById,
    latestTodoPlan,
    projectTitle,
    turnStopped = false,
  } = options ?? {};
  const isPlanCreate = item.name === CREATE_TODO_PLAN_TOOL_NAME;
  if (item.name !== TODO_TOOL_NAME && !isPlanCreate) return null;

  if (isPlanCreate) {
    const presentation = planCreatePresentation(item, latestTodoPlan);
    switch (presentation.kind) {
      case 'declined':
        return {
          kind: 'plan-card',
          declined: true,
          title: planCardTitle(presentation.proposed, intl, projectTitle),
          items: presentation.proposed.items,
          stopped: turnStopped,
        };
      case 'in-flight':
        return {
          kind: 'plan-card',
          title: planCardTitle(presentation.proposed, intl, projectTitle),
          items: presentation.proposed.items,
          stopped: turnStopped,
        };
      case 'anchor':
        return latestTodoPlan
          ? resolvePlanCardAtAnchor(item, latestTodoPlan, intl, projectTitle)
          : null;
      case 'pending-approval':
      case 'suppress':
        return null;
    }
  }

  // Pre-plan rows (`legacyIds`) skip the plan world entirely and fall through
  // to the legacy checklist below — the backend never backfills `todo_state`.
  if (latestTodoPlan && !latestTodoPlan.legacyIds.has(item.id)) {
    // Plan built via ungated `todo create` appends — the first todo result
    // that carried `todo_state` is the anchor and hosts the card.
    if (
      item.status === 'complete' &&
      showsPlanCard(item, planStateAt(latestTodoPlan, item.id))
    ) {
      return resolvePlanCardAtAnchor(item, latestTodoPlan, intl, projectTitle);
    }
    return null;
  }

  const operation = readTodoOperation(item.input);
  if (!operation) return null;

  const status = toAgentTodoStatus(item.status);
  if (!status) return null;

  const inputCount = countTodosInInput(operation, item.input);
  const labelCount = todoLabelCount(operation, inputCount);

  if (status === 'running') {
    return {
      kind: 'result-block',
      status: 'running',
      title: runningTodoLabel(intl, operation, labelCount),
      items: [],
    };
  }

  if (status === 'failed') {
    return {
      kind: 'result-block',
      status: 'failed',
      title: failedTodoLabel(intl, operation, labelCount),
      items: [],
      error: friendlyToolError(readToolResultText(item)),
    };
  }

  const parsed = parseAgentTodoResult(readToolResultText(item));
  if (!parsed) return null;

  const displayItems = latestTodoItemsById
    ? mergeTodoItemsWithLatest(parsed.items, latestTodoItemsById)
    : parsed.items;

  return {
    kind: 'result-block',
    status: 'completed',
    title: completedTodoLabel(intl, operation, labelCount),
    subtitle: buildTodoSubtitle(intl, {
      operation,
      input: item.input,
      items: displayItems,
      banner: parsed.banner,
    }),
    items: displayItems,
  };
};
